// Sign-out's half of the device lifecycle (encryption spec 6.2 "Logout wipes",
// 6.6 signed operations): tell the API this install is done, BEFORE the key it
// signs with is wiped.
//
// Read against spec 6.2 this looks backwards — that section wipes first and
// then "tells the API to mark the device revoked". Plainly: the revoke is a
// SIGNED operation, and the signature is made with the bundle signing key,
// which is stored wrapped UNDER MK. Wipe MK first and there is nothing left to
// sign with, so a wipe-first order can never produce the signed call the route
// requires. Only one of the two orders can work.
//
// It is also the safer way to fail. Failing AFTER the revoke leaves a device
// the server already refuses, which is fine. Failing after a wipe would leave
// an unrevoked device row still holding a wrapping of the key — the one outcome
// sign-out exists to prevent.
import { signerFromStore } from '@/utils/courses-encryption/device-approval';

/**
 * How long sign-out will wait for the revoke before giving up on it.
 *
 * Short on purpose. This runs between the member tapping Sign out and their key
 * material leaving the phone, so every millisecond here is a millisecond the
 * member is watching a button that has not done anything yet.
 */
export const REVOKE_TIMEOUT_MS = 4000;

/** Resolves to `null` if `work` has not settled in time; never leaves a timer behind. */
function withTimeout(work, timeoutMs) {
  let timer = null;
  const expiry = new Promise(resolve => {
    timer = setTimeout(() => resolve(null), timeoutMs);
  });
  return Promise.race([work, expiry]).finally(() => clearTimeout(timer));
}

/**
 * Tell the API this device is done, on the way out of a sign-out.
 *
 * **Never rejects, and never outlives its timeout.** Sign-out has to finish. A
 * member who taps Sign out on a phone with no signal, or against an API that
 * hangs, must still end up with their key wiped — key material left on a device
 * because a server call did not come back is far worse than a stale device row
 * the next login supersedes anyway.
 *
 * Must run before `courses-devices/reset` as well as before `courses-keys/wipe`:
 * that mutation drops the status snapshot the wrapped signing key is read from.
 *
 * @param {import('vuex').Store} store
 * @param {{ timeoutMs?: number, deps?: object }} [options] `deps` reaches `signerFromStore`
 * @returns {Promise<boolean>} whether the API confirmed the revoke, for a caller
 *   that wants to log it. Sign-out itself ignores the answer.
 */
export async function revokeThisDeviceOnLogout(
  store,
  { timeoutMs = REVOKE_TIMEOUT_MS, deps } = {},
) {
  const id = store?.state?.['courses-devices']?.localDevice?.id;
  // A member who never enrolled, or a device already wiped, has nothing to
  // revoke and no key to sign with. Not a failure — just not applicable.
  if (!id || !store.getters['courses-keys/keyMaterial']?.mk) return false;

  try {
    const signer = await signerFromStore({ store, deps });
    const done = await withTimeout(
      store.dispatch('courses-devices/revokeDevice', { id, signer }),
      timeoutMs,
    );
    // `null` is the timeout losing the race. The dispatch may still land later;
    // nothing waits for it, and the wipe that follows does not care either way.
    return done !== null;
  } catch (error) {
    return false;
  }
}
